import React, { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCmsPage, upsertCmsPage, uploadAdminImage, resolveMediaUrl } from '../../api';
import { PageHeader, TableSkeleton } from '../../components/ui';
import toast from 'react-hot-toast';
import { Plus, Trash2 } from 'lucide-react';

type FormValues = {
  titleEn: string;
  status: 'draft' | 'published';
  name: string;
  designation?: string;
  photoUrl?: string;
  bio?: string;
  welcomeMessage?: string;
  achievements: { value: string }[];
  services: { value: string }[];
  socialLinks: { platform: string; url: string }[];
  astrologerId?: string;
};

type ProfileKind = 'ceo-profile' | 'tarot-by-vrinda';

export default function CmsProfilePage({ slug, title }: { slug: ProfileKind; title: string }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-cms-one', slug],
    queryFn: () => getCmsPage(slug).then((r) => r.data.data.page).catch(() => null),
  });

  const { register, handleSubmit, reset, control, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      titleEn: title,
      status: 'draft',
      name: '',
      designation: '',
      photoUrl: '',
      bio: '',
      welcomeMessage: '',
      achievements: [],
      services: [],
      socialLinks: [],
      astrologerId: '',
    },
  });

  const achievements = useFieldArray({ control, name: 'achievements' });
  const services = useFieldArray({ control, name: 'services' });
  const socialLinks = useFieldArray({ control, name: 'socialLinks' });
  const photoUrl = watch('photoUrl');

  useEffect(() => {
    if (!data) return;
    const s = data.structured || {};
    reset({
      titleEn: data.title?.en || title,
      status: data.status || 'draft',
      name: s.name || '',
      designation: s.designation || '',
      photoUrl: s.photoUrl || '',
      bio: s.bio || '',
      welcomeMessage: s.welcomeMessage || '',
      achievements: (s.achievements || []).map((v: string) => ({ value: v })),
      services: (s.services || []).map((v: string) => ({ value: v })),
      socialLinks: (s.socialLinks || []).map((l: any) => ({ platform: l.platform, url: l.url })),
      astrologerId: s.astrologerId ? String(s.astrologerId) : '',
    });
  }, [data, reset, title]);

  const save = useMutation({
    mutationFn: (v: FormValues) =>
      upsertCmsPage(slug, {
        title: { en: v.titleEn },
        body: { en: v.bio || '' },
        status: v.status,
        structured: {
          name: v.name,
          designation: v.designation,
          photoUrl: v.photoUrl,
          bio: v.bio,
          welcomeMessage: v.welcomeMessage,
          achievements: v.achievements.map((a) => a.value).filter(Boolean),
          services: v.services.map((a) => a.value).filter(Boolean),
          socialLinks: v.socialLinks.filter((l) => l.platform && l.url),
          astrologerId: v.astrologerId || undefined,
        },
      }),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: ['admin-cms-one', slug] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  const onUpload = async (file: File) => {
    try {
      const r = await uploadAdminImage(file);
      const url = r.data?.data?.url || r.data?.url;
      if (url) setValue('photoUrl', url, { shouldDirty: true });
      toast.success('Photo uploaded');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Upload failed');
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <PageHeader title={title} />
        <TableSkeleton rows={6} cols={2} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      <PageHeader title={title} subtitle={`CMS slug: ${slug}`} />
      <form onSubmit={handleSubmit((v) => save.mutate(v))} className="card p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1">Page title</label>
            <input className="input" {...register('titleEn')} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Status</label>
            <select className="input" {...register('status')}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1">Name</label>
            <input className="input" {...register('name')} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Designation</label>
            <input className="input" {...register('designation')} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Photo</label>
          <div className="flex items-center gap-3">
            {photoUrl ? (
              <img src={resolveMediaUrl(photoUrl)} alt="" className="w-16 h-16 rounded-full object-cover" />
            ) : null}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
            />
          </div>
          <input type="hidden" {...register('photoUrl')} />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Bio</label>
          <textarea className="input" rows={4} {...register('bio')} />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Welcome message</label>
          <textarea className="input" rows={2} {...register('welcomeMessage')} />
        </div>
        {slug === 'tarot-by-vrinda' && (
          <div>
            <label className="block text-xs font-medium mb-1">Linked astrologer ID</label>
            <input className="input font-mono text-xs" {...register('astrologerId')} placeholder="Mongo ObjectId" />
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium">Achievements</label>
            <button type="button" className="btn-secondary btn-sm" onClick={() => achievements.append({ value: '' })}>
              <Plus size={12} /> Add
            </button>
          </div>
          {achievements.fields.map((f, i) => (
            <div key={f.id} className="flex gap-2 mb-2">
              <input className="input" {...register(`achievements.${i}.value`)} />
              <button type="button" className="btn-secondary btn-sm text-red-600" onClick={() => achievements.remove(i)}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium">Services</label>
            <button type="button" className="btn-secondary btn-sm" onClick={() => services.append({ value: '' })}>
              <Plus size={12} /> Add
            </button>
          </div>
          {services.fields.map((f, i) => (
            <div key={f.id} className="flex gap-2 mb-2">
              <input className="input" {...register(`services.${i}.value`)} />
              <button type="button" className="btn-secondary btn-sm text-red-600" onClick={() => services.remove(i)}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium">Social links</label>
            <button type="button" className="btn-secondary btn-sm" onClick={() => socialLinks.append({ platform: '', url: '' })}>
              <Plus size={12} /> Add
            </button>
          </div>
          {socialLinks.fields.map((f, i) => (
            <div key={f.id} className="grid grid-cols-[1fr_2fr_auto] gap-2 mb-2">
              <input className="input" placeholder="Platform" {...register(`socialLinks.${i}.platform`)} />
              <input className="input" placeholder="URL" {...register(`socialLinks.${i}.url`)} />
              <button type="button" className="btn-secondary btn-sm text-red-600" onClick={() => socialLinks.remove(i)}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function CeoProfilePage() {
  return <CmsProfilePage slug="ceo-profile" title="CEO Profile" />;
}

export function TarotByVrindaPage() {
  return <CmsProfilePage slug="tarot-by-vrinda" title="Tarot by Vrinda" />;
}
